/**
 * Admin install-backups RPC definitions (CLA-1071).
 *
 * Three endpoints, all admin-only (see {@link AdminMiddleware}):
 *
 *   - `admin/installs/backups.refresh` — ask the node to re-list its restic
 *     snapshots; the listing arrives asynchronously via
 *     `mgmt.reportBackupSnapshots`.
 *
 *   - `admin/installs/backups.restore` — dispatch a `backup.restore` command
 *     for one FULL restic snapshot id.
 *
 *   - `admin/installs/backups.watch` — streaming RPC emitting the install's
 *     {@link BackupsView} (listing + in-flight restore + last backup).
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";

import { AdminMiddleware } from "../admin.tags.ts";
import { BackupListError, BackupRestoreStep, BackupSnapshotWire, ResticSnapshotId } from "../backup-restore.schemas.ts";
import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized, ValkeyError } from "../errors.ts";

// ============================================================================
// Wire schemas
// ============================================================================

/**
 * An install's restore command as the admin UI tracks it.
 *
 * `safetySnapshotId` is known once the box has taken its pre-restore safety
 * snapshot; `step` is the most recent restore step reported.
 *
 * @since 1.0.0
 */
export const RestoreStatus = Schema.Struct({
  commandId: Schema.String,
  snapshotId: Schema.String,
  safetySnapshotId: Schema.NullOr(Schema.String),
  state: Schema.Literal("requested", "running", "restored", "rolled_back", "refused", "wedged"),
  step: Schema.NullOr(BackupRestoreStep),
  message: Schema.NullOr(Schema.String),
  at: Schema.DateTimeUtc,
});
export type RestoreStatus = Schema.Schema.Type<typeof RestoreStatus>;

/**
 * Everything the admin backups panel renders for one install.
 *
 * `listedAt` / `snapshots` / `listError` mirror the node's latest
 * `mgmt.reportBackupSnapshots` report (null / empty until one arrives).
 *
 * @since 1.0.0
 */
export const BackupsView = Schema.Struct({
  installationId: Schema.String,
  listedAt: Schema.NullOr(Schema.DateTimeUtc),
  snapshots: Schema.Array(BackupSnapshotWire),
  listError: Schema.NullOr(BackupListError),
  restore: Schema.NullOr(RestoreStatus),
  lastBackupAt: Schema.NullOr(Schema.DateTimeUtc),
  lastBackupStatus: Schema.NullOr(Schema.Literal("applied", "failed")),
});
export type BackupsView = Schema.Schema.Type<typeof BackupsView>;

/**
 * Why the api refused a backup refresh / restore before dispatching anything.
 *
 * @since 1.0.0
 */
export class BackupActionRefused extends Schema.TaggedError<BackupActionRefused>()("BackupActionRefused", {
  installationId: Schema.String,
  reason: Schema.Literal(
    "not-vps",
    "adopted",
    "not-active",
    "clawd-too-old",
    "unknown-snapshot",
    "snapshot-not-restorable",
    "restore-in-flight",
    "upgrade-in-flight",
  ),
  message: Schema.String,
}) {}

/**
 * Input for `backups.refresh` and `backups.watch`.
 *
 * @since 1.0.0
 */
export const BackupsInstallInput = Schema.Struct({
  installationId: Schema.String,
});
export type BackupsInstallInput = Schema.Schema.Type<typeof BackupsInstallInput>;

/**
 * Response for `backups.refresh` — the dispatched list command's id.
 *
 * @since 1.0.0
 */
export const BackupsRefreshResponse = Schema.Struct({
  commandId: Schema.String,
});
export type BackupsRefreshResponse = Schema.Schema.Type<typeof BackupsRefreshResponse>;

/**
 * Input for `backups.restore`. Only a FULL restic id is accepted.
 *
 * @since 1.0.0
 */
export const BackupsRestoreInput = Schema.Struct({
  installationId: Schema.String,
  snapshotId: ResticSnapshotId,
});
export type BackupsRestoreInput = Schema.Schema.Type<typeof BackupsRestoreInput>;

/**
 * Response for `backups.restore` — the dispatched restore command's id.
 *
 * @since 1.0.0
 */
export const BackupsRestoreResponse = Schema.Struct({
  commandId: Schema.String,
  snapshotId: ResticSnapshotId,
});
export type BackupsRestoreResponse = Schema.Schema.Type<typeof BackupsRestoreResponse>;

// ============================================================================
// RPC Group
// ============================================================================

const BackupActionError = Schema.Union(
  Unauthorized,
  Forbidden,
  RecordNotFoundError,
  DatabaseError,
  ValkeyError,
  BackupActionRefused,
);

/**
 * Admin Install-Backups RPC group. Tags are namespaced under
 * `admin/installs/`.
 *
 * @since 1.0.0
 */
export class InstallBackupsRpcs extends RpcGroup.make(
  Rpc.make("backups.refresh", {
    payload: BackupsInstallInput,
    success: BackupsRefreshResponse,
    error: BackupActionError,
  }).middleware(AdminMiddleware),
  Rpc.make("backups.restore", {
    payload: BackupsRestoreInput,
    success: BackupsRestoreResponse,
    error: BackupActionError,
  }).middleware(AdminMiddleware),
  Rpc.make("backups.watch", {
    payload: BackupsInstallInput,
    success: BackupsView,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, ValkeyError),
    stream: true,
  }).middleware(AdminMiddleware),
).prefix("admin/installs/") {}
